import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import posthog from '../services/posthog';
import Logo from '../components/common/Logo';
import toast from 'react-hot-toast';
import { VALID_US_ZIPS } from '../data/usZipCodes';
import { getZip5, isRealUsZip, isValidZipFormat } from '../utils/zipValidation';
import {
  ANALYTICS_EVENTS,
  trackAuthFailure,
  trackEvent,
  trackPageView,
} from '../utils/analytics';

const SITE_URL = 'https://ballotr.com';
const MIN_SIGNUP_AGE = 18;

const getMaxAllowedBirthday = () => {
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - MIN_SIGNUP_AGE,
    today.getMonth(),
    today.getDate()
  );

  return maxDate.toISOString().split('T')[0];
};

const isAtLeast18 = (birthday: string) => {
  if (!birthday) return false;

  const birthDate = new Date(`${birthday}T00:00:00`);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= MIN_SIGNUP_AGE;
};

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [showVerifyNotice, setShowVerifyNotice] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    first_name: '',
    last_name: '',
    birthday: '',
    gender: '',
    political_party: '',
    postal_code: '',
    country: 'United States',
  });

  useEffect(() => {
    const mode = searchParams.get('mode');

    if (mode === 'signup') {
      setIsSignUp(true);
    }

    if (mode === 'login') {
      setIsSignUp(false);
    }
  }, [searchParams]);

  useEffect(() => {
    trackPageView('auth');
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'postal_code') {
      const sanitized = value.replace(/[^0-9-]/g, '');
      setFormData((prev) => ({ ...prev, [name]: sanitized }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetUiState = () => {
    setError('');
    setShowResend(false);
  };

  const resetSignUpFields = () => {
    setFormData((prev) => ({
      ...prev,
      password: '',
      passwordConfirm: '',
      first_name: '',
      last_name: '',
      birthday: '',
      gender: '',
      political_party: '',
      postal_code: '',
      country: 'United States',
    }));
  };

  const handleResendVerification = async () => {
    const emailToUse = pendingVerificationEmail || formData.email;

    trackEvent(ANALYTICS_EVENTS.RESEND_VERIFICATION_CLICKED, {
      email_present: Boolean(emailToUse),
      auth_mode: isSignUp ? 'signup' : 'login',
    });

    try {
      if (!emailToUse) {
        toast.error('Enter your email address first.');
        return;
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: emailToUse,
        options: {
          emailRedirectTo: 'https://ballotr.com/',
        },
      });

      if (error) throw error;

      trackEvent(ANALYTICS_EVENTS.RESEND_VERIFICATION_SUCCEEDED, {
        auth_mode: isSignUp ? 'signup' : 'login',
      });

      toast.success('Verification email resent. Please check your inbox.');
    } catch (err: any) {
      console.error('Resend verification failed:', err?.message || err);

      trackEvent(ANALYTICS_EVENTS.RESEND_VERIFICATION_FAILED, {
        auth_mode: isSignUp ? 'signup' : 'login',
        error_message: err?.message || 'Failed to resend verification email.',
      });

      toast.error(err?.message || 'Failed to resend verification email.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setShowVerifyNotice(false);

    const {
      email,
      password,
      passwordConfirm,
      first_name,
      last_name,
      birthday,
      gender,
      political_party,
      postal_code,
      country,
    } = formData;

    if (
      isSignUp &&
      (!email ||
        !password ||
        !passwordConfirm ||
        !first_name ||
        !last_name ||
        !birthday ||
        !postal_code ||
        !country)
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    if (isSignUp && password !== passwordConfirm) {
      setError("Passwords don't match");
      return;
    }

    if (isSignUp && !isAtLeast18(birthday)) {
      setError('You must be at least 18 years old to create a Ballotr account.');
      return;
    }

    if (isSignUp && !isValidZipFormat(postal_code)) {
      trackEvent(ANALYTICS_EVENTS.ZIP_VALIDATION_FAILED, {
        reason: 'invalid_format',
        postal_code_length: postal_code.length,
      });

      setError('Please enter a valid ZIP code format.');
      return;
    }

    if (isSignUp && !isRealUsZip(postal_code, VALID_US_ZIPS)) {
      trackEvent(ANALYTICS_EVENTS.ZIP_VALIDATION_FAILED, {
        reason: 'not_real_us_zip',
        postal_code_prefix: postal_code.slice(0, 5),
      });

      setError('Please enter a real US ZIP code.');
      return;
    }

    setSubmitting(true);

    try {
      if (isSignUp) {
        const normalizedPostalCode = getZip5(postal_code);

        trackEvent(ANALYTICS_EVENTS.SIGNUP_SUBMITTED, {
          has_gender: Boolean(gender),
          has_political_party: Boolean(political_party),
          postal_code_prefix: normalizedPostalCode,
          country,
        });

        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: 'https://ballotr.com/',
            data: {
              first_name,
              last_name,
              birthday,
              gender: gender || null,
              political_party: political_party || null,
              postal_code: normalizedPostalCode,
              country,
            },
          },
        });

        if (signUpError) throw signUpError;

        const user = data?.user;

        if (user) {
          posthog.identify(user.id, {
            email: user.email,
          });
        }

        trackEvent(ANALYTICS_EVENTS.SIGNUP_SUCCEEDED, {
          has_gender: Boolean(gender),
          has_political_party: Boolean(political_party),
          postal_code_prefix: normalizedPostalCode,
          country,
        });

        setPendingVerificationEmail(email);
        setShowVerifyNotice(true);
        setIsSignUp(false);
        setShowResend(true);
        resetSignUpFields();

        toast.success('Account created.');

        return;
      }

      trackEvent(ANALYTICS_EVENTS.LOGIN_SUBMITTED, {
        email_present: Boolean(email),
      });

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      const user = data?.user;

      if (user) {
        posthog.identify(user.id, {
          email: user.email,
        });
      }

      trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCEEDED, {
        email_present: Boolean(email),
      });

      toast.success('Signed in successfully!');
      navigate('/');
    } catch (err: any) {
      const message = err?.message || 'Authentication failed.';
      console.error('Auth failed:', message);
      setError(message);

      trackAuthFailure({
        flow: isSignUp ? 'signup' : 'login',
        location: 'Auth.tsx',
        error: err,
        extraProperties: {
          auth_mode: isSignUp ? 'signup' : 'login',
          email_present: Boolean(email),
        },
      });

      if (message.toLowerCase().includes('email not confirmed')) {
        setShowResend(true);
        setShowVerifyNotice(true);
        setPendingVerificationEmail(email);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isSignUp ? 'Create Account | Ballotr' : 'Sign In | Ballotr'}</title>
        <meta
          name="description"
          content={
            isSignUp
              ? 'Create a Ballotr account to support or oppose campaigns and participate in civic action.'
              : 'Sign in to Ballotr to continue engaging with campaigns and civic action.'
          }
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`${SITE_URL}/auth`} />
      </Helmet>

      <main className="min-h-screen bg-secondary flex flex-col lg:flex-row">
        <div className="hidden lg:flex flex-col justify-center items-center w-full lg:w-1/2 bg-white p-8">
          <Logo showText className="h-10 mb-6" />

          <h2 className="text-2xl font-bold text-textPrimary">Welcome to Ballotr</h2>

          {isSignUp ? (
            <>
              <p className="text-textSecondary mt-3 text-center max-w-xs leading-relaxed">
                Turn political opinions into action.
                <br />
                Support or oppose campaigns and direct your contributions where they matter.
              </p>

              <p className="text-sm font-semibold text-textPrimary mt-4 tracking-wide">
                Engage. Decide. Act.
              </p>
            </>
          ) : (
            <p className="text-textSecondary mt-3 text-center max-w-xs">
              Sign in to continue taking action on the issues that matter.
            </p>
          )}
        </div>

        <div className="w-full lg:w-1/2 p-6 lg:p-12 flex items-center justify-center">
          <form
            onSubmit={handleSubmit}
            className="bg-white shadow-md rounded-lg w-full max-w-lg p-8 space-y-4"
          >
            <h2 className="text-xl font-semibold text-textPrimary mb-4">
              {isSignUp ? 'Sign Up' : 'Sign In'}
            </h2>

            {showVerifyNotice && pendingVerificationEmail && (
              <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm space-y-3">
                <div className="font-semibold text-green-800">
                  Check your email to verify your account
                </div>
                <p className="text-green-900">
                  We sent a verification link to <strong>{pendingVerificationEmail}</strong>. Please
                  verify your email before signing in.
                </p>
                <p className="text-green-800">
                  Didn’t get it? Check spam or promotions, or resend the verification email below.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="text-primary hover:underline font-medium"
                  >
                    Resend verification email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowVerifyNotice(false);
                      setError('');
                      setShowResend(false);
                    }}
                    className="text-gray-700 hover:underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              required
              autoComplete="email"
              className="w-full border px-4 py-2 rounded-md"
            />

            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              required
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              className="w-full border px-4 py-2 rounded-md"
            />

            {!isSignUp && (
              <div className="text-right text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="text-blue-600 hover:underline"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {isSignUp && (
              <>
                <input
                  type="password"
                  name="passwordConfirm"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  placeholder="Confirm Password"
                  required
                  autoComplete="new-password"
                  className="w-full border px-4 py-2 rounded-md"
                />

                <div className="grid grid-cols-2 gap-4">
                  <input
                    name="first_name"
                    placeholder="First Name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    autoComplete="given-name"
                    className="border px-4 py-2 rounded-md"
                  />
                  <input
                    name="last_name"
                    placeholder="Last Name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    autoComplete="family-name"
                    className="border px-4 py-2 rounded-md"
                  />
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birthday
                  </label>
                  <input
                    name="birthday"
                    type="date"
                    value={formData.birthday}
                    onChange={handleChange}
                    required
                    max={getMaxAllowedBirthday()}
                    className="w-full border px-4 py-2 rounded-md"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    You must be at least 18 years old to create an account.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    name="postal_code"
                    placeholder="ZIP Code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    required
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={10}
                    title="Enter a valid US ZIP code"
                    className="border px-4 py-2 rounded-md"
                  />
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    disabled
                    className="border px-4 py-2 rounded-md bg-gray-100"
                  >
                    <option value="United States">United States</option>
                  </select>
                </div>

                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full border px-4 py-2 rounded-md"
                >
                  <option value="">Select Gender (optional)</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>

                <select
                  name="political_party"
                  value={formData.political_party}
                  onChange={handleChange}
                  className="w-full border px-4 py-2 rounded-md"
                >
                  <option value="">Select Political Party (optional)</option>
                  <option value="Democratic">Democratic</option>
                  <option value="Republican">Republican</option>
                  <option value="Green">Green</option>
                  <option value="Libertarian">Libertarian</option>
                  <option value="Independent">Independent</option>
                  <option value="Other">Other</option>
                </select>
              </>
            )}

            {error && (
              <div className="text-sm text-red-600 space-y-2">
                <p>{error}</p>

                {showResend && !showVerifyNotice && (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="text-blue-600 hover:underline"
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-white py-2 rounded-md hover:bg-ballotr-blue-light transition disabled:opacity-60"
            >
              {submitting ? 'Please wait…' : isSignUp ? 'Create Account' : 'Log In'}
            </button>

            <p className="text-sm text-center text-textSecondary">
              {isSignUp ? 'Already have an account?' : 'Don’t have an account?'}{' '}
              <button
                type="button"
                onClick={() => {
                  const nextMode = !isSignUp;

                  trackEvent(ANALYTICS_EVENTS.AUTH_MODE_TOGGLED, {
                    mode: nextMode ? 'signup' : 'login',
                  });

                  setIsSignUp(nextMode);
                  resetUiState();
                  setShowVerifyNotice(false);
                }}
                className="text-primary hover:underline"
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </button>
            </p>
          </form>
        </div>
      </main>
    </>
  );
};

export default Auth;